/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, TemplateRef, ViewChild} from '@angular/core';
import {AuthenticationService} from './core/authentication/authentication.service';
import {UserService} from './core/user/user.service';
import {UsageTrackingService} from './core/usage-tracking/usage-tracking.service';
import {AppConfigService} from './core/config/app-config.service';
import {FabUser} from '@fabric/common/types';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {Router} from '@angular/router';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  public readonly appName: string = 'Vacation Tracker';
  public readonly appVersion: string;
  public readonly appVersionCodename: string;
  public readonly apiMap: any;
  public readonly buildNumber: string;
  public readonly environmentName: string;
  public user: FabUser;
  public userAuthorities: string[] = [];

  public countryValues = [{value:'IRL', displayValue: 'Ireland'}, {value:'GBR', displayValue:'United Kingdom of Great Britain and Northern Ireland (the)'}];
  public selectedCountry;
  @ViewChild('countryDialog') countryDialog!: TemplateRef<any>;

  constructor(
    private readonly authenticationService: AuthenticationService,
    public matDialog: MatDialog,
    private readonly router: Router,
    private readonly usageTrackingService: UsageTrackingService,
    public readonly userService: UserService,
    private readonly configService: AppConfigService
  ) {
      // Enable Auth Silent Renew
    this.authenticationService.enableSilentRenew();

      // Setup app info config.
    const appConfig = this.configService.getConfig();
    this.appName = appConfig.appName;
    this.appVersion = appConfig.appVersion ?? '';
    this.appVersionCodename = appConfig.appVersionCodename ?? '';
    this.apiMap = appConfig.apiMap;
    this.buildNumber = appConfig.buildNumber;
    this.environmentName = appConfig.environmentName ?? '';

    // Provide userInfo with the logged-in user
    this.user = this.userService.user ?? ({} as FabUser);

    this.selectedCountry = localStorage.getItem('CountryCode');
    if (!this.selectedCountry) {
      localStorage.setItem('CountryCode', 'IRL');
    }

    // Get User authorities
    this.userAuthorities = this.user.authorities ?? [];

    // Web usage analytics
    this.usageTrackingService.start({
      applicationName: 'Vacation Tracker',
      asmsId: 173575,
      asmsWebsiteId: 173575,
      environment: appConfig.environmentName ?? '',
      gmin: this.userService?.user?.gmin ?? '',
      uiFramework: 'Fabric',
    });
  }

    /**
     * Opens the pop-up modal.
     * Retrieves the countryCode from local storage.
     * Sets the width and height of the modal.
     */
    public openModal(): void {
      this.selectedCountry = localStorage.getItem('CountryCode');
      let multiplyValue = 0.4;
      const dialogConfig = new MatDialogConfig();
      dialogConfig.hasBackdrop = true;
      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;
      if (window.innerHeight > 700) {
        multiplyValue = 0.3;
        dialogConfig.maxHeight = window.innerHeight * multiplyValue;
        dialogConfig.minHeight = window.innerHeight * multiplyValue;
        dialogConfig.minWidth = window.innerWidth * 0.05;

      } else {
        dialogConfig.maxHeight = window.innerHeight * multiplyValue;
        dialogConfig.minHeight = window.innerHeight * multiplyValue;
        dialogConfig.minWidth = window.innerWidth * 0.05;
      }

      this.matDialog.open(this.countryDialog, dialogConfig);
    }

    /**
     * Save your newly selected Country.
     * Page the user is on gets re called so the CountryCode gets updated with the new country.
     * (not a full page refresh)
     *
     * @param countryCode
     */
    public save(countryCode: string): void {
      localStorage.setItem('CountryCode', countryCode);
      const currentRoute = this.router.url;

      this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
        this.router.navigate([currentRoute]); // navigate to same route
      });
      this.matDialog.closeAll();
    }

    /**
     * Option to Close/Cancel the pop-up modal without saving any changes.
     */
    close() {
      this.matDialog.closeAll();
    }

}
